import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { BehaviorSubject, lastValueFrom, Observable, Subject } from "rxjs";
import { environment } from "src/environments/environment";
import { TopListEntry } from "../models/toplist_entry";
import { HistoryEntry } from "../models/history_entry";
import { PopoverController } from "@ionic/angular";
import { HumidorPopoverLoadingComponent } from "../popovers/humidor-popover-loading/humidor-popover-loading.component";
import { YesNoPopupComponent } from "../popovers/yes-no-popup/yes-no-popup.component";
import { DisplayloadingService } from "./displayloading.service";

@Injectable({
  providedIn: "root",
})
export class StatisticsService {
  private api = environment.apiUrl;
  private topListEntries = new Subject<TopListEntry[]>();
  private historyEntries: HistoryEntry[] = [];
  private historyEntriesUpdated = new Subject<HistoryEntry[]>();
  private historyPercLoaded = new BehaviorSubject<number>(0);
  private historyEntriesPrice = new Subject<number>();
  private loadingPop: any;

  constructor(
    private http: HttpClient,
    private popc: PopoverController,
    private ls: DisplayloadingService,
  ) {}
  /**
   * Gets top list entries
   * @returns top list entries as Observable
   */
  public getTopListEntries(): Observable<TopListEntry[]> {
    this.http
      .get<any>(`${this.api}/v2/toplist/get_toplist.php`)
      .subscribe((entries) => {
        this.topListEntries.next(entries.toplist);
      });
    return this.topListEntries.asObservable();
  }

  /**
   * Adds smoked cigar to history
   * @param cigarId
   * @param price
   * @returns true if cigar has been added to history
   */
  public async addToHistory(cigarId: number, price: number): Promise<void> {
    const today = new Date();
    const clientDate =
      today.getFullYear() +
      "-" +
      String(today.getMonth() + 1).padStart(2, "0") +
      "-" +
      String(today.getDate()).padStart(2, "0");
    await lastValueFrom(this.http
      .post<any>(this.api + "/v2/smoking_history/add.php", {
        cigarId:    cigarId,
        cigarPrice: price,
        clientDate: clientDate,
    }));
  }
  /**
   * Gets smoking history for current user
   * @returns nothing, Subject will be updated instead
   */
  public async fetchHistory(offset: number = 0, amount: number = 1000) {
    if (offset === 0) {
      this.historyEntries = [];
      this.historyPercLoaded.next(0);
      //Popover für Anzeige des Ladestatus in Prozent
      this.loadingPop = await this.popc.create({
        component: HumidorPopoverLoadingComponent,
        componentProps: {
          loaded: this.historyPercLoaded.asObservable(),
          popoverType: "stats",
        },
        backdropDismiss: false,
        id: "loadingPop",
      });
      await this.loadingPop.present();
    }
    this.http
      .get<any>(`${this.api}/v2/smoking_history/get.php`, {
        params: {
          offset: offset,
          amount: amount,
        }
      })
      .subscribe(async (result) => {
        if (result.complete === true) {
          this.historyEntries = this.historyEntries.concat(
            result.historyEntries,
          );
          //this.percLoaded.next(1);
          this.historyEntriesUpdated.next(this.historyEntries);
          this.historyEntriesPrice.next(parseFloat(result.costs));
          await this.loadingPop.dismiss();
        }
        //Ein Teil der Einträge (amount) wurde geladen
        else {
          this.historyEntries = this.historyEntries.concat(
            result.historyEntries,
          );
          this.fetchHistory(offset + 1000);
          this.historyPercLoaded.next(
            this.historyPercLoaded.getValue() + amount / result.total,
          );
          this.historyEntriesUpdated.next(this.historyEntries);
        }
      });
  }
  /**
   * Deletes history entry
   * @param entryId
   */
  public async deleteHistoryEntry(entryId: number): Promise<boolean> {
    let deleted = false;
    //1st ask for permission
    const ynp = await this.popc.create({
      component: YesNoPopupComponent,
      componentProps: { questionType: "delete-history-entry" },
    });
    await ynp.present();
    const data = await ynp.onDidDismiss();
    if (typeof data.data.answer !== "undefined") {
      if (data.data.answer === true) {
        //2nd delete from component and DOM
        for (let i = 0; i < this.historyEntries.length; i++) {
          if (this.historyEntries[i].id === entryId) {
            this.historyEntries.splice(i, 1);
            this.historyEntriesUpdated.next(this.historyEntries);
            break;
          }
        }
        //3rd delete from database
        this.http.delete(`${this.api}/v2/smoking_history/delete.php`, {
            observe: "response",
            params: { he_id: entryId }
          })
          .subscribe((response) => {
          if (response && response.status === 204) {
              this.ls.presentMessage(
                "displayloading-service.history-del",
                1000,
                "success",
              );
            }
          });
        deleted = true;
      }
    }
    return deleted;
  }

  //gibt Subject als abbonierbares Observable zurück
  public getEntriesUpdateListener() {
    return this.historyEntriesUpdated.asObservable();
  }
  //gibt Subject als abbonierbares Observable zurück
  public getHistoryPriceUpdateListener() {
    return this.historyEntriesPrice.asObservable();
  }
}
